/**
 * Preflop open/fold ranges encoded from `sheets/15-preflop-range-reference.md`.
 *
 * The 8 positions collapse to 3 unique opening ranges (early / middle / late)
 * plus two minor variants at the late bucket — BTN opens T9o and 98o, SB does
 * not. Source: sheet 15 § "Position Buckets" and the position sections.
 *
 * Used by the Open/Fold quiz (`/quizzes/open-fold`) and any future quiz that
 * needs to ask "is this hand in position X's range?". If sheet 15 changes,
 * this file must change with it — otherwise quiz answers drift from the
 * coaching source of truth.
 *
 * 9-handed cash, 100bb. Sheet 1 notes 6-handed adjustments ("UTG ≈ HJ in
 * 9-handed"); we don't model 6-handed in v1 of the quiz.
 */

export type Position = "UTG" | "UTG+1" | "UTG+2" | "MP" | "HJ" | "CO" | "BTN" | "SB";

export const POSITIONS: readonly Position[] = [
  "UTG", "UTG+1", "UTG+2", "MP", "HJ", "CO", "BTN", "SB",
] as const;

/**
 * Standard poker shorthand. Pairs are "77", suited is "AKs", offsuit is "AKo".
 * High card always first ("AK", never "KA"). Total: 13 pairs + 78 suited +
 * 78 offsuit = 169 unique hands.
 */
export type HandNotation = string;

const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"] as const;
export type Rank = (typeof RANKS)[number];

const RANK_INDEX: Record<Rank, number> = RANKS.reduce(
  (acc, r, i) => ({ ...acc, [r]: i }),
  {} as Record<Rank, number>,
);

/** Enumerate every 169-hand notation in canonical (rank-descending) order. */
export function allHands(): HandNotation[] {
  const hands: HandNotation[] = [];
  for (let i = 0; i < RANKS.length; i++) {
    for (let j = 0; j < RANKS.length; j++) {
      const hi = RANKS[i]!;
      const lo = RANKS[j]!;
      if (i === j) hands.push(`${hi}${lo}`); // pair
      else if (i < j) hands.push(`${hi}${lo}s`); // suited (high card first)
      else hands.push(`${lo}${hi}o`); // offsuit (high card first)
    }
  }
  // Dedup — pair cell visited once on the diagonal, but offsuit cells get
  // visited twice (once above, once below the diagonal) by the j<i branch
  // generating `${lo}${hi}o`. Set dedup handles it.
  return Array.from(new Set(hands));
}

// --- Range definitions ---------------------------------------------------

// EARLY: UTG, UTG+1, UTG+2 — 15/15 per sheet 15
// "Pairs 77+, suited aces AJs+, suited broadways KQs, offsuit broadways AQo+"
const EARLY: ReadonlySet<HandNotation> = new Set([
  // Pairs 77+
  "AA", "KK", "QQ", "JJ", "TT", "99", "88", "77",
  // Suited aces AJs+
  "AKs", "AQs", "AJs",
  // Suited broadways KQs
  "KQs",
  // Offsuit broadways AQo+
  "AKo", "AQo",
]);

// MIDDLE: MP, HJ, CO — 22/22
// "Pairs 55+, any suited ace, K9s+, QJs/JTs, 76s+, AJo+/KQo"
const MIDDLE: ReadonlySet<HandNotation> = new Set([
  // Pairs 55+
  "AA", "KK", "QQ", "JJ", "TT", "99", "88", "77", "66", "55",
  // Suited aces — any
  "AKs", "AQs", "AJs", "ATs", "A9s", "A8s", "A7s", "A6s", "A5s", "A4s", "A3s", "A2s",
  // Suited kings K9s+
  "KQs", "KJs", "KTs", "K9s",
  // Suited broadways QJs, JTs
  "QJs", "JTs",
  // Suited connectors 76s+ (76s, 87s, 98s, T9s — JTs+ already covered above)
  "76s", "87s", "98s", "T9s",
  // Offsuit broadways AJo+, KQo
  "AKo", "AQo", "AJo", "KQo",
]);

// LATE base: shared by BTN + SB at 35/35
// "any pair, any suited ace/king, Q5s+, any suited connector, T8s+ one-gappers,
//  A8o+, K9o+, any offsuit broadway"
const LATE_BASE: ReadonlySet<HandNotation> = new Set([
  // Pairs — any
  "AA", "KK", "QQ", "JJ", "TT", "99", "88", "77", "66", "55", "44", "33", "22",
  // Suited aces — any
  "AKs", "AQs", "AJs", "ATs", "A9s", "A8s", "A7s", "A6s", "A5s", "A4s", "A3s", "A2s",
  // Suited kings — any
  "KQs", "KJs", "KTs", "K9s", "K8s", "K7s", "K6s", "K5s", "K4s", "K3s", "K2s",
  // Suited queens Q5s+
  "QJs", "QTs", "Q9s", "Q8s", "Q7s", "Q6s", "Q5s",
  // Suited connectors — any (32s through JTs)
  "32s", "43s", "54s", "65s", "76s", "87s", "98s", "T9s", "JTs",
  // Suited one-gappers T8s+ (T8s, J9s — Q9s/K9s/A9s already covered above)
  "T8s", "J9s",
  // Offsuit aces A8o+
  "AKo", "AQo", "AJo", "ATo", "A9o", "A8o",
  // Offsuit kings K9o+
  "KQo", "KJo", "KTo", "K9o",
  // Offsuit broadways — any (QJo, QTo, JTo)
  "QJo", "QTo", "JTo",
]);

// BTN extends LATE_BASE with the two offsuit connectors sheet 15 allows only there.
const BTN: ReadonlySet<HandNotation> = new Set([...LATE_BASE, "T9o", "98o"]);
const SB: ReadonlySet<HandNotation> = LATE_BASE;

const RANGE_BY_POSITION: Record<Position, ReadonlySet<HandNotation>> = {
  "UTG": EARLY,
  "UTG+1": EARLY,
  "UTG+2": EARLY,
  "MP": MIDDLE,
  "HJ": MIDDLE,
  "CO": MIDDLE,
  "BTN": BTN,
  "SB": SB,
};

export function isInOpenRange(position: Position, hand: HandNotation): boolean {
  return RANGE_BY_POSITION[position].has(hand);
}

// --- Rationale prose (used by the quiz wrong-answer reveal) -------------

export type RangeBucket = "EARLY" | "MIDDLE" | "LATE";

export function bucketForPosition(position: Position): RangeBucket {
  if (position === "UTG" || position === "UTG+1" || position === "UTG+2") return "EARLY";
  if (position === "MP" || position === "HJ" || position === "CO") return "MIDDLE";
  return "LATE";
}

/**
 * Short prose summary of the opening range for a bucket, lifted from sheet 15.
 * Used as the "reason" line on quiz reveal — keeps the quiz coupled to the
 * coaching source-of-truth instead of inventing new explanations.
 */
export function bucketSummary(bucket: RangeBucket): string {
  switch (bucket) {
    case "EARLY":
      return "Early position (UTG / UTG+1 / UTG+2) plays a 15/15 range: pairs 77+, suited aces AJs+, KQs, offsuit broadways AQo+. Premiums only.";
    case "MIDDLE":
      return "Middle position (MP / HJ / CO) plays a 22/22 range: pairs 55+, any suited ace, K9s+, QJs/JTs, suited connectors 76s+, offsuit broadways AJo+ and KQo.";
    case "LATE":
      return "Late position (BTN / SB) plays a 35/35 range: any pair, any suited ace/king, Q5s+, any suited connector, T8s+ one-gappers, A8o+, K9o+, any offsuit broadway. T9o and 98o are BTN-only.";
  }
}

// --- Hand parsing utilities (used by UI rendering) ----------------------

export interface ParsedHand {
  high: Rank;
  low: Rank;
  suited: boolean;
  isPair: boolean;
}

/**
 * Parse "AKs" / "77" / "AKo" into structured form. Returns null for malformed
 * input. The quiz never feeds malformed hands in (we generate from `allHands()`)
 * but keep this defensive in case a caller comes in from elsewhere.
 */
export function parseHand(hand: HandNotation): ParsedHand | null {
  if (hand.length === 2) {
    const r = hand[0] as Rank;
    if (!(r in RANK_INDEX) || hand[0] !== hand[1]) return null;
    return { high: r, low: r, suited: false, isPair: true };
  }
  if (hand.length === 3) {
    const hi = hand[0] as Rank;
    const lo = hand[1] as Rank;
    const suit = hand[2];
    if (!(hi in RANK_INDEX) || !(lo in RANK_INDEX)) return null;
    if (RANK_INDEX[hi] >= RANK_INDEX[lo]) return null; // hi must be higher rank (lower index)
    if (suit !== "s" && suit !== "o") return null;
    return { high: hi, low: lo, suited: suit === "s", isPair: false };
  }
  return null;
}
